import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { GetSignInUrlParameters } from '../../../../../../typings/api/integrations/leica/rest/v1/auth';
import { TokenService } from '../../../../services/authentication/token.service';
import { LeicaAxiosServiceFactory } from '../../../../services/axios/leica-axios-service-factory';
import { EnvironmentService } from '../../../../services/environment/environment.service';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { RequestParamsToApiPaths } from '../../../../utils/typescript-utils';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { Hyperlink } from '../../hyperlink/hyperlink';
import { InfoBox } from '../../info-box/info-box';
import { DeleteIntegrationDialog } from '../dialogs/delete-integration-dialog/delete-integration-dialog';
import { IntegrationTitle } from '../integration-title';
import { IntegrationStatus, LeicaIntegrationItem } from '../state/integrations-state';
import { IntegrationGrants } from './integration-grants';
import { resolveIssueCodeTranslation } from './issue-code-to-local-key';
import { Component, Content, Footer, InfoItem, InfoItems } from './style';

enum Status {
  Idle,
  Pending,
  Error,
}

interface Props {
  item: LeicaIntegrationItem;
}

const generateLeicaProjectLink = (leicaProjectId: string) => {
  if (EnvironmentService.instance.isProduction()) {
    return `https://conx.leica-geosystems.com/frontend/site#/ict/account/${leicaProjectId}/project/track/assets`;
  } else {
    return `https://staging.conx.leica-geosystems.com/frontend/site#/ict/account/${leicaProjectId}/project/track/assets`;
  }
};

const LeicaIntegration = (props: Props) => {
  const { t } = useTranslation('integrations');
  const { project } = React.useContext(ProjectContext);
  const [status, setStatus] = React.useState(Status.Idle);
  const { handleError, buildErrorList } =
    useErrorHandling<RequestParamsToApiPaths<GetSignInUrlParameters>>();
  const deleteIntegrationDialog = useDialog();

  const signIn = async () => {
    try {
      setStatus(Status.Pending);
      const response = await LeicaAxiosServiceFactory.instance.createAuthServiceV1().getSignInUrl({
        query: {
          skyMapAuthToken: new TokenService().get(),
          skyMapProjectId: project.id,
          successRedirectUrl: window.location.href,
        },
      });

      document.location.assign(response.redirectUrl);
    } catch (err) {
      setStatus(Status.Error);
      handleError(err);
    }
  };

  switch (props.item.status) {
    case IntegrationStatus.Active: {
      return (
        <Component>
          <IntegrationTitle>{props.item.name}</IntegrationTitle>

          <InfoBox
            bottomMargin={true}
            color="yellow"
            leftIcon={{ icon: ['fad', 'info'] }}
            topMargin={true}
            width="fit-content"
          >
            <Trans
              components={{ bold: <strong />, br: <br /> }}
              i18nKey="provider.common.fileManagerInfo"
              ns="integrations"
            />
          </InfoBox>

          <Content>
            <InfoItems>
              <InfoItem>
                <label id="activated">
                  {t('provider.common.activated', { ns: 'integrations' })}
                </label>
                <span aria-labelledby="activated">
                  {formattedDateTime(props.item.model!.createdAt)}
                </span>
              </InfoItem>

              <InfoItem>
                <label id="projectName">
                  {t('provider.common.projectName', { ns: 'integrations' })}
                </label>
                <span aria-labelledby="projectName">{props.item.model!.leicaProjectName}</span>
              </InfoItem>

              <InfoItem>
                <label id="projectUrl">
                  {t('provider.common.projectLink', { ns: 'integrations' })}
                </label>
                <span aria-labelledby="projectUrl">
                  <Hyperlink url={generateLeicaProjectLink(props.item.model!.leicaProjectId)} />
                </span>
              </InfoItem>
              <IntegrationGrants integration="LEICA" />
            </InfoItems>
          </Content>

          {isDefined(props.item.issues) &&
            props.item.issues.map((issue, index) => (
              <InfoBox
                bottomMargin={true}
                color={
                  issue.severity === 'error'
                    ? 'red'
                    : issue.severity === 'warning'
                      ? 'yellow'
                      : 'blue'
                }
                key={index}
                leftIcon={{ icon: ['fad', 'exclamation'] }}
                topMargin={true}
                width="fit-content"
              >
                {resolveIssueCodeTranslation(issue.code)}
              </InfoBox>
            ))}

          <Footer>
            <Button
              color="primary"
              disabled={!isDefined(props.item.model?.id)}
              loading={status === Status.Pending}
              variant="contained"
              onClick={() => deleteIntegrationDialog.show()}
            >
              {t('provider.common.removeIntegration', { ns: 'integrations' })}
            </Button>
          </Footer>

          {deleteIntegrationDialog.render(
            <DeleteIntegrationDialog
              item={props.item}
              onClose={() => deleteIntegrationDialog.hide()}
            />,
          )}
        </Component>
      );
    }
    case IntegrationStatus.Setup:
      return (
        <Component>
          <IntegrationTitle>{props.item.name}</IntegrationTitle>

          <InfoBox
            color="yellow"
            leftIcon={{ icon: ['fad', 'info'] }}
            topMargin={true}
            width="fit-content"
          >
            <Trans
              components={{ bold: <strong /> }}
              i18nKey="provider.leica.setupInfo"
              ns="integrations"
              values={{ name: props.item.name }}
            />
          </InfoBox>

          <Content>
            {buildErrorList({
              infoBoxProps: {
                bottomMargin: true,
                width: 'fit-content',
              },
            })}

            <Button
              color="primary"
              loading={status === Status.Pending}
              type="submit"
              variant="contained"
              onClick={signIn}
            >
              {t('provider.common.activateIntegration', { ns: 'integrations' })}
            </Button>
          </Content>
        </Component>
      );
    default:
      return <Component>{t('provider.common.loadError', { ns: 'integrations' })}</Component>;
  }
};

export { LeicaIntegration };
